import React, { useEffect, useState, useCallback } from 'react';
import { GraduationCap, BookOpen, ExternalLink, Sparkles } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useAcademicYear } from '../../context/AcademicYearContext';
import { useT } from '../../i18n/LanguageContext';
import { schoolApi } from '../../api/school';
import { oustazApi, fileUrl } from '../../api/oustaz';
import { PageHeader, EmptyState, LoadingState, Card } from '../../components/school/ui';
import { ReportCardView } from '../../components/school/ReportCardView';
import { AttendanceHistory } from '../../components/school/AttendanceHistory';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';

export default function StudentPortalPage() {
  const { user } = useAuth();
  const { academicYears, selectedYear } = useAcademicYear();
  const { t } = useT();
  const orgId = user?.organization_id;
  // Students are always scoped to the ACTIVE academic year (never past years).
  const activeYearId = (academicYears.find((y) => y.is_active) || selectedYear || {})?.id || null;
  const [student, setStudent] = useState(null);
  const [exams, setExams] = useState([]);
  const [examId, setExamId] = useState('');
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noProfile, setNoProfile] = useState(false);
  // Dual-program: which enrolled class is being viewed + report scope
  const [enrollments, setEnrollments] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [scope, setScope] = useState('term'); // 'term' | 'general'

  // Lessons (OUSTAZ.AI)
  const [lessons, setLessons] = useState([]);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [activeLesson, setActiveLesson] = useState(null);

  const init = useCallback(async () => {
    try {
      setLoading(true);
      const me = await schoolApi.myStudent(activeYearId);
      setStudent(me);
      const enr = Array.isArray(me?.enrollments) ? me.enrollments : [];
      setEnrollments(enr);
      setSelectedClassId(enr[0]?.class_id || me?.class_id || '');
      const ex = await schoolApi.listExams(orgId, activeYearId ? { academic_year_id: activeYearId } : {});
      // Always order Trimestre 1 -> 2 -> 3 (backend returns them newest-first).
      const trimNum = (e) => {
        if (e?.term_sequence != null) return Number(e.term_sequence);
        const m = String(e?.term_name || e?.name || '').match(/(\d+)/);
        return m ? Number(m[1]) : 99;
      };
      const sorted = [...(ex || [])].sort((a, b) => trimNum(a) - trimNum(b) || String(a.name || '').localeCompare(String(b.name || '')));
      setExams(sorted);
      setExamId(sorted.length ? sorted[0].id : '');
    } catch (e) {
      if (e?.response?.status === 404) setNoProfile(true);
      else toast.error('Failed to load');
    } finally { setLoading(false); }
  }, [orgId, activeYearId]);

  useEffect(() => { init(); }, [init]);

  const selectedEnrollment = enrollments.find((e) => e.class_id === selectedClassId) || null;

  useEffect(() => {
    if (!student) return;
    if (scope === 'term' && !examId) { setCard(null); return; }
    const params = {
      organization_id: orgId,
      student_id: student.id,
      class_id: selectedEnrollment?.class_id || undefined,
      section_id: selectedEnrollment?.section_id || undefined,
      exam_id: scope === 'term' ? examId : undefined,
      all_terms: scope === 'general',
    };
    schoolApi.getReportCard(params).then(setCard).catch(() => setCard(null));
  }, [examId, student, orgId, selectedClassId, scope]); // eslint-disable-line

  const loadLessons = useCallback(async () => {
    setLessonsLoading(true);
    try {
      const data = await oustazApi.studentLessons();
      setLessons(Array.isArray(data) ? data : []);
    } catch {
      setLessons([]);
    } finally { setLessonsLoading(false); }
  }, []);

  useEffect(() => { if (student) loadLessons(); }, [student, loadLessons]);

  if (loading) return <div className="max-w-[1400px] mx-auto"><LoadingState rows={6} /></div>;
  if (noProfile) return <div className="max-w-[1400px] mx-auto"><Card><EmptyState icon={GraduationCap} title={t('portal.noProfile')} /></Card></div>;

  return (
    <div className="max-w-[1400px] mx-auto">
      <PageHeader title={t('portal.studentTitle')} subtitle={`${student?.name} · ${selectedEnrollment?.class_name || student?.class_name || ''}${(selectedEnrollment?.section_name || student?.section_name) ? ' / ' + (selectedEnrollment?.section_name || student?.section_name) : ''}`} icon={GraduationCap} />
      <Tabs defaultValue="report" className="space-y-4">
        <TabsList data-testid="student-portal-tabs" className="w-full sm:w-auto overflow-x-auto">
          <TabsTrigger value="report" data-testid="tab-report-card">{t('nav.reportCards')}</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance">{t('att.myTitle')}</TabsTrigger>
          <TabsTrigger value="lessons" data-testid="tab-lessons" className="gap-1.5"><BookOpen className="w-3.5 h-3.5" />{t('portal.lessons')}</TabsTrigger>
        </TabsList>

        <TabsContent value="report" className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-end gap-2">
            {enrollments.length > 1 && (
              <Select value={selectedClassId} onValueChange={setSelectedClassId}>
                <SelectTrigger className="w-full sm:w-60" data-testid="student-class-select">
                  <SelectValue placeholder={t('portal.selectClass', 'Select class')} />
                </SelectTrigger>
                <SelectContent>
                  {enrollments.map((e) => (
                    <SelectItem key={e.class_id} value={e.class_id}>
                      {e.class_name}{e.section_name ? ` / ${e.section_name}` : ''}{e.program_label ? ` · ${e.program_label}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="w-full sm:w-52" data-testid="student-scope-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="term">{t('rc.scopeTerm', 'Single Trimester')}</SelectItem>
                <SelectItem value="general">{t('rc.scopeGeneral', 'General (All Terms)')}</SelectItem>
              </SelectContent>
            </Select>
            {scope === 'term' && (
              <Select value={examId} onValueChange={setExamId}>
                <SelectTrigger className="w-full sm:w-56" data-testid="student-exam-select"><SelectValue placeholder={t('portal.selectExam')} /></SelectTrigger>
                <SelectContent>{exams.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          {card ? <ReportCardView card={card} /> : <Card><EmptyState icon={GraduationCap} title={t('common.noData')} description={scope === 'general' ? t('rc.generalHint', 'Aggregates all trimesters into one cumulative report.') : t('portal.selectExam')} /></Card>}
        </TabsContent>

        <TabsContent value="attendance">
          <AttendanceHistory />
        </TabsContent>

        <TabsContent value="lessons" className="space-y-4">
          {lessonsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="lessons-loading">
              {[0, 1, 2].map((i) => (
                <div key={i} className="rounded-xl border bg-card overflow-hidden">
                  <Skeleton className="w-full h-36" />
                  <div className="p-4 space-y-2"><Skeleton className="h-4 w-3/4" /><Skeleton className="h-8 w-full" /></div>
                </div>
              ))}
            </div>
          ) : lessons.length === 0 ? (
            <Card><EmptyState icon={BookOpen} title={t('portal.lessonsEmpty')} /></Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="student-lessons-grid">
              {lessons.map((g) => (
                <div key={g.gen_id} className="group rounded-xl border bg-card overflow-hidden flex flex-col hover:shadow-md transition-shadow" data-testid={`student-lesson-${g.gen_id}`}>
                  <div className="relative w-full h-36 bg-muted flex items-center justify-center overflow-hidden">
                    {g.thumbnail_url ? (
                      <img src={fileUrl(g.thumbnail_url)} alt={g.title} className="w-full h-full object-cover" />
                    ) : (
                      <Sparkles className="w-10 h-10 text-muted-foreground/40" />
                    )}
                  </div>
                  <div className="p-4 flex flex-col gap-2 flex-1">
                    <Badge variant="outline" className="self-start">{g.content_type_label || g.content_type}</Badge>
                    <p className="text-sm font-semibold leading-snug line-clamp-2" title={g.title}>{g.title}</p>
                    <div className="mt-auto pt-2">
                      <Button size="sm" className="w-full gap-1.5" onClick={() => setActiveLesson(g)} data-testid={`student-lesson-open-${g.gen_id}`}>
                        <ExternalLink className="w-3.5 h-3.5" />{t('portal.openLesson')}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!activeLesson} onOpenChange={(o) => { if (!o) setActiveLesson(null); }}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[92vh] p-0 gap-0 flex flex-col" data-testid="lesson-viewer-dialog">
          <DialogHeader className="px-4 py-3 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2 text-base">
              <BookOpen className="w-4 h-4 text-primary" />
              <span className="truncate">{activeLesson?.title}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 bg-white">
            {activeLesson?.html_url ? (
              <iframe
                key={activeLesson.gen_id}
                src={fileUrl(activeLesson.html_url)}
                title={activeLesson.title}
                className="w-full h-full border-0"
                sandbox="allow-scripts allow-same-origin allow-popups"
                data-testid="lesson-viewer-iframe"
              />
            ) : (
              <div className="h-full flex items-center justify-center"><EmptyState icon={BookOpen} title={t('common.noData')} /></div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
